import { eventQBus } from "../types/EventQBus";
import { ConfigurableFacet } from "@otto-ec/heureka-components/facet";
import NextBestAction from "../nextBestAction/NextBestAction.svelte";
import { Assets } from "@otto-ec/heureka-components/filter";
import { mount } from "svelte";
import type { ConfigurableFilterOptions } from "@otto-ec/heureka-components/filter";
import { setActiveConfiguration } from "@otto-ec/heureka-components/filter";
import { mapElements } from "../util/Utils";
import { isMobile } from "../util/Breakpoint";
import { CustomRangeInput } from "./slider/CustomRangeInput";
import { events } from "../events";
import { isAnyActive } from "../toggle/Toggle";

/*                                                                                                            */
const config: Record<string, ConfigurableFilterOptions> = {
  basecolor: {
    filterConfig: {
      default: {
        filterTitleMode: "full",
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            type: "grid",
            facetValueConfig: {
              type: "chip",
              facetValueLabelConfig: {
                type: "color",
                titleSize: "100",
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        filterTitleMode: "full",
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            type: "overflowBox",
            facetValueConfig: {
              type: "chip",
              facetValueLabelConfig: {
                type: "color",
                titleSize: "75",
              },
            },
          },
        },
      },
    },
  },
  customerreview: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            facetValueConfig: {
              type: "radio",
              facetValueLabelConfig: {
                type: "rating",
              },
            },
          },
        },
      },
    },
  },
  materialdescription: {
    filterConfig: {
      default: {
        facetConfig: {
          facetValueGroupConfig: {
            facetValueConfig: {
              facetValueLabelConfig: {
                type: "text",
              },
            },
          },
        },
      },
    },
  },
  brand: {
    filterConfig: {
      default: {
        filterTitleMode: "onlyPopularValuesAndTitle",
        facetConfig: {
          type: "scrollable",
          searchable: true,
          facetValueGroupConfig: {
            type: "list",
            facetValueConfig: {
              type: "checkbox",
              facetValueLabelConfig: {
                type: "text",
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        filterTitleMode: "onlyPopularValuesAndTitle",
        facetConfig: {
          type: "scrollable",
          searchable: true,
          facetValueGroupConfig: {
            type: "overflowBox",
            facetValueConfig: {
              type: "chip",
              facetValueLabelConfig: {
                type: "text",
                showCount: false,
                size: "75",
              },
            },
          },
        },
      },
      sheetSecondLevel: {
        filterTitleMode: "onlyPopularValuesAndTitle",
        facetConfig: {
          searchable: true,
          facetValueGroupConfig: {
            facetValueConfig: {
              facetValueLabelConfig: {
                type: "text",
              },
            },
          },
        },
      },
    },
  },
  sale: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            facetValueConfig: {
              type: "toggle",
              facetValueLabelConfig: {
                type: "text",
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        filterTitleMode: "full",
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            facetValueConfig: {
              type: "toggle",
              facetValueLabelConfig: {
                type: "text",
              },
            },
          },
        },
      },
    },
  },
  retailprice: {
    filterConfig: {
      default: {
        filterTitleMode: "full",
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "grid",
            gridCellMinSize: "79",
            containsRanges: true,
            facetValueConfig: {
              type: "singleSelectRange",
              facetValueLabelConfig: {
                type: "text",
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        filterTitleMode: "popularValuesExclusion",
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "grid",
            gridCellMinSize: "79",
            facetValueConfig: {
              type: "singleSelectRange",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
    },
  },
  defaultvermassungsfacet: {
    filterConfig: {
      default: {
        filterTitleMode: "full",
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "grid",
            gridCellMinSize: "90",
            containsRanges: true,
            facetValueConfig: {
              type: "range",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        filterTitleMode: "onlyTitle",
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "grid",
            gridCellMinSize: "90",
            facetValueConfig: {
              type: "range",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
      sheetSecondLevel: {
        filterTitleMode: "full",
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "overflowBox",
            containsRanges: true,
            facetValueConfig: {
              type: "range",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
    },
  },
};

function initializeConfigurableFacet() {
  /*                                                                                                       */
  setActiveConfiguration(isMobile() ? "sheetFirstLevel" : "default");
  mapElements(".ts_heureka_svelteFacet", (elem) => {
    const parentElement = elem.parentElement as HTMLElement;
    const { filterId, facetId, facetVisibility, serialized } = elem.dataset;
    const filterConfig =
      config[facetId.toLowerCase()] || (filterId === "vermassung" && config["defaultvermassungsfacet"]);
    mount(ConfigurableFacet, {
      target: parentElement,
      props: {
        data: {
          ...JSON.parse(serialized),
          showTitle: elem.dataset.showTitle === "true",
          useSlider: elem.dataset.mayHideSlider !== "true" || (facetId === "retailprice" && isMobile()),
          parentId: `find_filter_${filterId}`,
          filterId: filterId,
        },
        visibility: facetVisibility,
        ...filterConfig,
      },
    });
    elem.remove();
    return parentElement;
  }).map(CustomRangeInput.init);
}

export function initializeNextBestActionComponent() {
  mapElements(".ts_heureka_svelteNextBestAction", (elem) => {
    const parentElement = elem.parentElement as HTMLElement;
    const { serialized } = elem.dataset;
    mount(NextBestAction, {
      target: parentElement,
      props: {
        ...JSON.parse(serialized),
        containsOnlyNbaValues: elem.dataset.containsOnlyNbaValues === "true",
      },
    });
    elem.remove();
    return parentElement;
  });
}

function initializeAssets() {
  mount(Assets, {
    target: document.body,
  });
}

export default function registerSvelteComponentInitialization() {
  events.onFilterSectionLoaded(initializeConfigurableFacet);
  if (isMobile() && isAnyActive("HEUREKA_1760_NBA_HOME_AND_LIVING", "HEUREKA_1760_NBA_HOME_AND_LIVING_E3504")) {
    events.onFilterSectionLoaded(initializeNextBestActionComponent);
  }
  eventQBus.once("heureka.filterSection.loaded", initializeAssets);
}
